import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { useOrgProject } from '@/context/OrgProjectContext/useOrgProject.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { HumanDecisionInboxItem, HumanDecisionRequest } from '@/types/runs.ts';
import { PROJECT_HUMAN_DECISIONS_KEY, RUN_HUMAN_DECISIONS_KEY } from '@/utils/queryKeys.ts';
import {
  get_project_human_decisions_url,
  get_run_human_decisions_url,
  human_decision_action_url,
  human_decision_claim_url,
  human_decision_release_claim_url,
  resolve_human_decision_url,
} from '@/utils/urls.ts';

export type ResolveHumanDecisionPayload = {
  response_payload: Record<string, unknown>;
  response_reason: string;
  expected_revision: number;
};

export const useHumanDecisions = (runId?: string) => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();

  return useQuery<HumanDecisionRequest[], AxiosError>({
    queryKey: RUN_HUMAN_DECISIONS_KEY(automationId, runId),
    queryFn: async () => (await apiClient.get(get_run_human_decisions_url(runId))).data,
    enabled: !!runId,
  });
};

export const useProjectHumanDecisions = (projectId?: string) => {
  const apiClient = useApiClient();
  return useQuery<HumanDecisionInboxItem[], AxiosError>({
    queryKey: PROJECT_HUMAN_DECISIONS_KEY(projectId),
    queryFn: async () => (await apiClient.get(get_project_human_decisions_url(projectId))).data,
    enabled: !!projectId,
  });
};

export const useResolveHumanDecision = () => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const queryClient = useQueryClient();
  const { projectId } = useOrgProject();

  return useMutation<
    HumanDecisionRequest,
    AxiosError,
    { decisionId: string; runId: string; payload: ResolveHumanDecisionPayload }
  >({
    mutationFn: async ({ decisionId, payload }) =>
      (await apiClient.post(resolve_human_decision_url(decisionId), payload)).data,
    onSuccess: async (_decision, { runId }) => {
      await queryClient.invalidateQueries({
        queryKey: RUN_HUMAN_DECISIONS_KEY(automationId, runId),
      });
      await queryClient.invalidateQueries({ queryKey: PROJECT_HUMAN_DECISIONS_KEY(projectId) });
    },
  });
};

export const useTerminalizeHumanDecision = () => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const queryClient = useQueryClient();
  const { projectId } = useOrgProject();

  return useMutation<
    HumanDecisionRequest,
    AxiosError,
    {
      decisionId: string;
      runId: string;
      action: 'reject' | 'cancel';
      reason: string;
      expectedRevision: number;
    }
  >({
    mutationFn: async ({ decisionId, action, reason, expectedRevision }) =>
      (
        await apiClient.post(human_decision_action_url(decisionId, action), {
          reason,
          expected_revision: expectedRevision,
        })
      ).data,
    onSuccess: async (_decision, { runId }) => {
      await queryClient.invalidateQueries({
        queryKey: RUN_HUMAN_DECISIONS_KEY(automationId, runId),
      });
      await queryClient.invalidateQueries({ queryKey: PROJECT_HUMAN_DECISIONS_KEY(projectId) });
    },
  });
};

type ClaimHumanDecisionVariables = { decisionId: string; runId: string };

export const useClaimHumanDecision = () => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const { projectId } = useOrgProject();
  const queryClient = useQueryClient();
  return useMutation<HumanDecisionRequest, AxiosError, ClaimHumanDecisionVariables>({
    mutationFn: async ({ decisionId }) =>
      (await apiClient.post(human_decision_claim_url(decisionId))).data,
    onSuccess: async (_decision, { runId }) => {
      await queryClient.invalidateQueries({
        queryKey: RUN_HUMAN_DECISIONS_KEY(automationId, runId),
      });
      await queryClient.invalidateQueries({ queryKey: PROJECT_HUMAN_DECISIONS_KEY(projectId) });
    },
  });
};

export const useReleaseHumanDecisionClaim = () => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const { projectId } = useOrgProject();
  const queryClient = useQueryClient();
  return useMutation<HumanDecisionRequest, AxiosError, ClaimHumanDecisionVariables>({
    mutationFn: async ({ decisionId }) =>
      (await apiClient.post(human_decision_release_claim_url(decisionId))).data,
    onSuccess: async (_decision, { runId }) => {
      await queryClient.invalidateQueries({
        queryKey: RUN_HUMAN_DECISIONS_KEY(automationId, runId),
      });
      await queryClient.invalidateQueries({ queryKey: PROJECT_HUMAN_DECISIONS_KEY(projectId) });
    },
  });
};
